import React, { useMemo } from 'react';
import {
  AnnotationDrawing as AnnotationDrawingType,
  CrystalDrawing as CrystalDrawingType,
  DrawingType,
  DrawingUpdateDetails,
  DropLocationDrawing as DropLocationDrawingType,
  OverlayTools,
  POIDrawing as POIDrawingType,
  ROIDrawing as ROIDrawingType,
  RulerDrawing as RulerDrawingType,
  ScribbleDrawing as ScribbleDrawingType,
  SelectedDrawingDetails,
  poiPointSize,
} from './constants';
import { DrawingsData } from 'redux/reducers/wellReducer';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import CrystalDrawing from './Drawings/crystalDrawing';
import PoiDrawing from './Drawings/poiDrawing';
import RoiDrawing from './Drawings/roiDrawing';
import AnnotationDrawing from './Drawings/annotationDrawing';
import ScribbleDrawing from './Drawings/scribbleDrawing';
import { getRulerSizeText } from './utils';
import RulerDrawing from './Drawings/rulerDrawing';
import { cloneDeep } from 'lodash';
import DropLocationDrawing from './Drawings/dropLocationDrawing';

interface Props extends DrawingsData {
  selectedDrawing: SelectedDrawingDetails;
  tpObject: TransformPoints;
  zoomScale: number;
  updateDrawing: (
    drawingId: number,
    drawingType: DrawingType,
    drawingUpdateDetails: DrawingUpdateDetails,
  ) => void;
  saveAndUpdateDrawing: (
    drawingId: number,
    drawingType: DrawingType,
    drawingUpdateDetails: DrawingUpdateDetails,
  ) => void;
  selectDrawing: (drawingId: number, drawingType: DrawingType) => void;
  regionName?: string;
  selectedDrawingTool: OverlayTools;
  selectDrawingTool: (drawingTool: OverlayTools) => void;
  hideDrawings?: boolean;
}
const DrawingRenderer = (props: Props) => {
  const {
    selectedDrawing,
    tpObject,
    crystals,
    pois,
    rois,
    annotations,
    scribbles,
    rulers,
    lightPaths,
    dropLocations,
    zoomScale,
    updateDrawing,
    saveAndUpdateDrawing,
    selectDrawing,
    regionName,
    selectedDrawingTool,
    selectDrawingTool,
    hideDrawings = false,
  } = props;

  const { drawingId: selectedDrawingId, drawingType: selectedDrawingType } =
    selectedDrawing || {};

  const shouldNotRenderDrawing = (isNewDrawing: boolean) => {
    return !tpObject || (hideDrawings && !isNewDrawing);
  };

  const renderCrystal = (crystal: CrystalDrawingType, drawingIndex: number) => {
    if (shouldNotRenderDrawing(crystal.isNewDrawing)) {
      return null;
    }
    return (
      <CrystalDrawing
        key={`crystal-drawing-${crystal.id}`}
        x={crystal.x}
        y={crystal.y}
        height={crystal.height}
        width={crystal.width}
        tpObject={tpObject}
        index={drawingIndex}
        id={crystal.id}
        isChecked={crystal.isChecked}
        isSelected={
          crystal.id === selectedDrawingId && selectedDrawingType === DrawingType.CRYSTAL
        }
        updateDrawing={updateDrawing}
        saveAndUpdateDrawing={saveAndUpdateDrawing}
        selectDrawing={selectDrawing}
        isLocked={crystal.locked}
        selectDrawingTool={selectDrawingTool}
      />
    );
  };

  const renderPoi = (poi: POIDrawingType, drawingIndex: number) => {
    if (shouldNotRenderDrawing(poi.isNewDrawing)) {
      return null;
    }
    return (
      <PoiDrawing
        key={`poi-drawing-${poi.id}`}
        x={poi.x}
        y={poi.y}
        height={poiPointSize + 5}
        width={poiPointSize + 5}
        tpObject={tpObject}
        index={drawingIndex}
        id={poi.id}
        isSelected={
          poi.id === selectedDrawingId && selectedDrawingType === DrawingType.POI
        }
        updateDrawing={updateDrawing}
        saveAndUpdateDrawing={saveAndUpdateDrawing}
        selectDrawing={selectDrawing}
        name={poi.name}
        isLocked={poi.locked}
        selectDrawingTool={selectDrawingTool}
        color={poi.color}
      />
    );
  };

  const renderRoi = (roi: ROIDrawingType, drawingIndex: number) => {
    if (shouldNotRenderDrawing(roi.isNewDrawing)) {
      return null;
    }
    const isDrawingSelected =
      roi.id === selectedDrawingId && selectedDrawingType === DrawingType.ROI;
    return (
      <RoiDrawing
        key={`roi-drawing-${roi.id}`}
        id={roi.id}
        x={roi.x}
        y={roi.y}
        width={roi.width}
        height={roi.height}
        regionName={roi.regionName}
        isImagingEnabled={roi.imagingEnabled}
        isLocked={roi.locked}
        isSelected={isDrawingSelected}
        tpObject={tpObject}
        updateDrawing={updateDrawing}
        saveAndUpdateDrawing={saveAndUpdateDrawing}
        selectDrawing={selectDrawing}
        index={drawingIndex}
        lightPaths={cloneDeep(lightPaths)}
        selectDrawingTool={selectDrawingTool}
        shouldRenderLightPaths={roi.isNewDrawing || isDrawingSelected}
      />
    );
  };

  const renderAnnotation = (annotation: AnnotationDrawingType, drawingIndex: number) => {
    if (shouldNotRenderDrawing(annotation.isNewDrawing)) {
      return null;
    }
    const {
      id,
      x,
      y,
      width,
      height,
      bgColor,
      fontFamily,
      fontStyle,
      size,
      color,
      text,
      textDecoration,
      expanded,
    } = annotation;
    return (
      <AnnotationDrawing
        key={`annotation-drawing-${id}`}
        x={x}
        y={y}
        width={width}
        height={height}
        id={id}
        backgroundColor={bgColor}
        fontFamily={fontFamily}
        fontStyle={fontStyle}
        index={drawingIndex}
        isLocked={false}
        isSelected={
          annotation.id === selectedDrawingId &&
          selectedDrawingType === DrawingType.Annotation
        }
        size={size}
        updateDrawing={updateDrawing}
        saveAndUpdateDrawing={saveAndUpdateDrawing}
        selectDrawing={selectDrawing}
        text={text}
        textColor={color}
        textDecoration={textDecoration}
        tpObject={tpObject}
        zoomScale={zoomScale}
        expanded={expanded}
        selectDrawingTool={selectDrawingTool}
      />
    );
  };

  const renderScribble = (scribble: ScribbleDrawingType, drawingIndex: number) => {
    if (shouldNotRenderDrawing(scribble.isNewDrawing)) {
      return null;
    }
    const { id, x, y, points, color } = scribble;
    return (
      <ScribbleDrawing
        key={`scribble-drawing-${id}`}
        points={points}
        color={`rgb(${color})`}
        id={id}
        index={drawingIndex}
        x={x}
        y={y}
        width={0}
        height={0}
        tpObject={tpObject}
        isSelected={
          id === selectedDrawingId && selectedDrawingType === DrawingType.Scribble
        }
        isLocked={false}
        updateDrawing={updateDrawing}
        saveAndUpdateDrawing={saveAndUpdateDrawing}
        selectDrawing={selectDrawing}
        selectDrawingTool={selectDrawingTool}
      />
    );
  };

  const renderRuler = (ruler: RulerDrawingType, drawingIndex: number) => {
    if (shouldNotRenderDrawing(ruler.isNewDrawing)) {
      return null;
    }
    return (
      <RulerDrawing
        key={`ruler-drawing-${ruler.id}`}
        id={ruler.id}
        x={ruler.x1}
        y={ruler.y1}
        xEnd={ruler.x2}
        yEnd={ruler.y2}
        rulerSizeText={getRulerSizeText(ruler, tpObject)}
        color={ruler.color}
        tpObject={tpObject}
        index={drawingIndex}
        isSelected={
          ruler.id === selectedDrawingId && selectedDrawingType === DrawingType.Ruler
        }
        isLocked={false}
        updateDrawing={updateDrawing}
        saveAndUpdateDrawing={saveAndUpdateDrawing}
        selectDrawing={selectDrawing}
        allowResize={selectedDrawingTool === OverlayTools.POINTER}
        selectDrawingTool={selectDrawingTool}
      />
    );
  };

  const renderDropLocation = (
    dropLocation: DropLocationDrawingType,
    drawingIndex: number,
  ) => {
    if (shouldNotRenderDrawing(dropLocation.isNewDrawing)) {
      return null;
    }
    return (
      <DropLocationDrawing
        id={dropLocation.id}
        key={`drop-location-${dropLocation.id}`}
        x={dropLocation.x}
        y={dropLocation.y}
        width={dropLocation.width}
        height={dropLocation.height}
        index={drawingIndex}
        isLocked={false}
        isSelected={
          dropLocation.id === selectedDrawingId &&
          selectedDrawingType === DrawingType.DropLocation
        }
        tpObject={tpObject}
        updateDrawing={updateDrawing}
        saveAndUpdateDrawing={saveAndUpdateDrawing}
        selectDrawing={selectDrawing}
        lightPaths={cloneDeep(lightPaths)}
        selectDrawingTool={selectDrawingTool}
      />
    );
  };

  const allDrawings = useMemo(() => {
    const mergedArray = [
      ...(rois?.length ? rois.map((item) => ({ ...item, type: DrawingType.ROI })) : []),
      ...(pois?.length ? pois.map((item) => ({ ...item, type: DrawingType.POI })) : []),
      ...(rulers?.length
        ? rulers.map((item) => ({ ...item, type: DrawingType.Ruler }))
        : []),
      ...(scribbles?.length
        ? scribbles.map((item) => ({ ...item, type: DrawingType.Scribble }))
        : []),
      ...(annotations?.length
        ? annotations.map((item) => ({ ...item, type: DrawingType.Annotation }))
        : []),
      ...(crystals?.length
        ? crystals.map((item) => ({ ...item, type: DrawingType.CRYSTAL }))
        : []),
      ...(regionName === 'Overview' && dropLocations?.length
        ? dropLocations.map((item) => ({ ...item, type: DrawingType.DropLocation }))
        : []),
    ]?.sort((a, b) => a.zIndex - b.zIndex);

    return mergedArray?.map((item, index) => {
      const { type, ...rest } = item;
      switch (type) {
        case DrawingType.CRYSTAL:
          return renderCrystal(rest as CrystalDrawingType, index);
        case DrawingType.ROI:
          return renderRoi(rest as ROIDrawingType, index);
        case DrawingType.POI:
          return renderPoi(rest as POIDrawingType, index);
        case DrawingType.Annotation:
          return renderAnnotation(rest as AnnotationDrawingType, index);
        case DrawingType.Scribble:
          return renderScribble(rest as ScribbleDrawingType, index);
        case DrawingType.Ruler:
          return renderRuler(rest as RulerDrawingType, index);
        case DrawingType.DropLocation:
          return renderDropLocation(rest as DropLocationDrawingType, index);
      }
    });
  }, [
    crystals,
    rois,
    pois,
    annotations,
    scribbles,
    rulers,
    dropLocations,
    tpObject,
    selectedDrawing,
    hideDrawings,
    zoomScale,
  ]);

  return <>{allDrawings}</>;
};

export default DrawingRenderer;
